/**
 * Lets a hook navigate when it may be rendered outside a router (#360).
 * `useNavigate` throws outside one, and hooks cannot be called conditionally,
 * so a hook keeps a ref and renders <NavigateBridge>, which fills the ref only
 * when there is a router. Outside one the ref stays null.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';

export type NavigateRef = MutableRefObject<((url: string) => void) | null>;

export function useNavigateRef(): NavigateRef {
  return useRef<((url: string) => void) | null>(null);
}

function Bridge({ navRef }: { navRef: NavigateRef }) {
  const navigate = useNavigate();
  useEffect(() => {
    navRef.current = (url: string) => { void navigate(url); };
    return () => { navRef.current = null; };
  }, [navigate, navRef]);
  return null;
}

export function NavigateBridge({ navRef }: { navRef: NavigateRef }) {
  return useInRouterContext() ? <Bridge navRef={navRef} /> : null;
}
